<template>
  <div class="flex items-center justify-center">
    <div class="w-full max-w-sm p-6 sm:p-8">
      <h2 class="text-2xl font-bold text-center text-gray-800 mb-6">Change Password</h2>

      <form @submit.prevent="changePassword">
        <div class="space-y-4 mb-6">
          <!-- Old Password -->
          <div>
            <label for="oldPassword" class="block text-sm font-medium text-gray-700 mb-1">Old Password</label>
            <Password
              id="oldPassword"
              v-model="oldPassword"
              toggleMask
              :class="{'p-invalid': errors.oldPassword}"
              class="w-full"
              :feedback="false"
              fluid
              placeholder="Enter your current password"
            />
            <small class="p-error">{{ errors.oldPassword }}</small>
          </div>

          <!-- New Password with Strength Meter -->
          <div>
            <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <Password
              id="newPassword"
              v-model="newPassword"
              :class="{'p-invalid': errors.newPassword}"
              toggleMask
              class="w-full"
              fluid
              placeholder="Enter your new password"
              :strongRegex="strongPasswordRegex"
              :mediumRegex="mediumPasswordRegex"
              :promptLabel="passwordPromptLabel"
              :weakLabel="passwordWeakLabel"
              :mediumLabel="passwordMediumLabel"
              :strongLabel="passwordStrongLabel"
              @input="validateNewPassword"
            />
            <small class="p-error">{{ errors.newPassword }}</small>

            <!-- Password Requirements -->
            <div v-if="newPassword" class="mt-2 space-y-1">
              <div class="text-xs">
                <div :class="passwordRequirements.length ? 'text-green-600' : 'text-red-500'" class="flex items-center">
                  <i :class="passwordRequirements.length ? 'pi pi-check' : 'pi pi-times'" class="mr-1"></i>
                  At least 8 characters
                </div>
                <div :class="passwordRequirements.uppercase ? 'text-green-600' : 'text-red-500'" class="flex items-center">
                  <i :class="passwordRequirements.uppercase ? 'pi pi-check' : 'pi pi-times'" class="mr-1"></i>
                  One uppercase letter
                </div>
                <div :class="passwordRequirements.lowercase ? 'text-green-600' : 'text-red-500'" class="flex items-center">
                  <i :class="passwordRequirements.lowercase ? 'pi pi-check' : 'pi pi-times'" class="mr-1"></i>
                  One lowercase letter
                </div>
                <div :class="passwordRequirements.number ? 'text-green-600' : 'text-red-500'" class="flex items-center">
                  <i :class="passwordRequirements.number ? 'pi pi-check' : 'pi pi-times'" class="mr-1"></i>
                  One number
                </div>
                <div :class="passwordRequirements.special ? 'text-green-600' : 'text-red-500'" class="flex items-center">
                  <i :class="passwordRequirements.special ? 'pi pi-check' : 'pi pi-times'" class="mr-1"></i>
                  One special character (!@#$%^&*)
                </div>
              </div>
            </div>
          </div>

          <!-- Confirm New Password -->
          <div>
            <label for="confirmNewPassword" class="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <Password
              id="confirmNewPassword"
              v-model="confirmNewPassword"
              :class="{'p-invalid': errors.confirmNewPassword || (confirmNewPassword && !passwordsMatch)}"
              :feedback="false"
              toggleMask
              fluid
              class="w-full"
              placeholder="Confirm your new password"
              @input="validateConfirmPassword"
            />
            <small class="p-error">{{ errors.confirmNewPassword }}</small>

            <!-- Password Match Indicator -->
            <div v-if="confirmNewPassword" class="mt-1">
              <div v-if="passwordsMatch" class="text-green-600 text-xs flex items-center">
                <i class="pi pi-check mr-1"></i>
                Passwords match
              </div>
              <div v-else class="text-red-500 text-xs flex items-center">
                <i class="pi pi-times mr-1"></i>
                Passwords do not match
              </div>
            </div>
          </div>
        </div>

        <!-- Password Strength Indicator -->
        <div v-if="newPassword" class="mb-6">
          <div class="text-sm font-medium text-gray-700 mb-2">Password Strength:</div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              :class="passwordStrengthClass"
              class="h-2 rounded-full transition-all duration-300"
              :style="{ width: passwordStrengthWidth }"
            ></div>
          </div>
          <div class="text-xs mt-1" :class="passwordStrengthTextClass">
            {{ passwordStrengthText }}
          </div>
        </div>

        <Button
          type="submit"
          label="Change Password"
          :loading="loading"
          :disabled="!isFormValid"
          class="w-full p-button-primary"
        />
      </form>
    </div>

    <!-- Modern Dialog Alert -->
    <div v-if="showDialog" class="dialog-overlay" @click="closeDialog">
      <div class="dialog-container" @click.stop>
        <div class="dialog-content" :class="dialogType === 'success' ? 'success-dialog' : 'error-dialog'">
          <!-- Header -->
          <div class="dialog-header">
            <div class="dialog-icon-container">
              <div v-if="dialogType === 'success'" class="success-icon-wrapper">
                <i class="pi pi-check dialog-icon success-icon"></i>
                <div class="success-circle"></div>
                <div class="success-circle-bg"></div>
              </div>
              <div v-else class="error-icon-wrapper">
                <i class="pi pi-times dialog-icon error-icon"></i>
                <div class="error-circle"></div>
                <div class="error-circle-bg"></div>
              </div>
            </div>

            <button @click="closeDialog" class="dialog-close-btn">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="dialog-body">
            <h3 class="dialog-title">
              {{ dialogType === 'success' ? 'Password Changed Successfully!' : 'Password Change Failed' }}
            </h3>
            <p class="dialog-message">
              {{ dialogMessage }}
            </p>
          </div>

          <!-- Footer -->
          <div class="dialog-footer">
            <button
              @click="closeDialog"
              class="dialog-action-btn"
              :class="dialogType === 'success' ? 'success-btn' : 'error-btn'"
            >
              <i class="pi pi-check mr-2"></i>
              {{ dialogType === 'success' ? 'Continue' : 'Try Again' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Password from 'primevue/password';
import Button from 'primevue/button';
import { useUserStore } from '@/store/admin/UserAPI'
import { useAuthStore } from '@/auth/auth'

const authStore = useAuthStore()
const userStore = useUserStore()

const emit = defineEmits(['close'])

// State Management
const oldPassword = ref('');
const newPassword = ref('');
const confirmNewPassword = ref('');
const errors = ref({});
const loading = ref(false);

// Dialog state
const showDialog = ref(false);
const dialogType = ref('success'); // 'success' or 'error'
const dialogMessage = ref('');

// Password validation patterns
const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
const mediumPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*]{6,}$/;

// Password prompt labels
const passwordPromptLabel = ref('Enter a password');
const passwordWeakLabel = ref('Weak');
const passwordMediumLabel = ref('Medium');
const passwordStrongLabel = ref('Strong');

// Password requirements tracking
const passwordRequirements = computed(() => ({
  length: newPassword.value.length >= 8,
  uppercase: /[A-Z]/.test(newPassword.value),
  lowercase: /[a-z]/.test(newPassword.value),
  number: /\d/.test(newPassword.value),
  special: /[!@#$%^&*]/.test(newPassword.value)
}));

// Password strength calculation
const passwordStrength = computed(() => {
  if (!newPassword.value) return 0;

  let score = 0;
  const requirements = passwordRequirements.value;

  if (requirements.length) score++;
  if (requirements.uppercase) score++;
  if (requirements.lowercase) score++;
  if (requirements.number) score++;
  if (requirements.special) score++;

  return score;
});

const passwordStrengthClass = computed(() => {
  const strength = passwordStrength.value;
  if (strength <= 2) return 'bg-red-500';
  if (strength <= 3) return 'bg-yellow-500';
  if (strength <= 4) return 'bg-blue-500';
  return 'bg-green-500';
});

const passwordStrengthWidth = computed(() => {
  return `${(passwordStrength.value / 5) * 100}%`;
});

const passwordStrengthText = computed(() => {
  const strength = passwordStrength.value;
  if (strength <= 2) return 'Weak - Consider using a stronger password';
  if (strength <= 3) return 'Fair - Add more character types';
  if (strength <= 4) return 'Good - Almost there!';
  return 'Strong - Great password!';
});

const passwordStrengthTextClass = computed(() => {
  const strength = passwordStrength.value;
  if (strength <= 2) return 'text-red-500';
  if (strength <= 3) return 'text-yellow-600';
  if (strength <= 4) return 'text-blue-500';
  return 'text-green-500';
});

// Password match checking
const passwordsMatch = computed(() => {
  return newPassword.value && confirmNewPassword.value && newPassword.value === confirmNewPassword.value;
});

// Form validation
const isFormValid = computed(() => {
  return oldPassword.value &&
         newPassword.value &&
         confirmNewPassword.value &&
         passwordStrength.value === 5 &&
         passwordsMatch.value &&
         Object.keys(errors.value).length === 0;
});

// Dialog functions
const showSuccessDialog = (message) => {
  dialogType.value = 'success';
  dialogMessage.value = message;
  showDialog.value = true;
};

const showErrorDialog = (message) => {
  dialogType.value = 'error';
  dialogMessage.value = message;
  showDialog.value = true;
};

const closeDialog = () => {
  showDialog.value = false;
  // Auto-focus back to form if error
  if (dialogType.value === 'error') {
    setTimeout(() => {
      const firstInput = document.querySelector('#oldPassword input');
      if (firstInput) {
        firstInput.focus();
      }
    }, 300);
  }
  else{
    emit('close')
  }
};

// Validation functions
const validateNewPassword = () => {
  if (!newPassword.value) {
    errors.value.newPassword = 'New password is required.';
    return;
  }

  if (newPassword.value.length < 8) {
    errors.value.newPassword = 'Password must be at least 8 characters long.';
    return;
  }

  if (!strongPasswordRegex.test(newPassword.value)) {
    errors.value.newPassword = 'Password must meet all requirements shown below.';
    return;
  }

  if (newPassword.value === oldPassword.value) {
    errors.value.newPassword = 'New password must be different from the old password.';
    return;
  }

  delete errors.value.newPassword;

  // Revalidate confirm password if it exists
  if (confirmNewPassword.value) {
    validateConfirmPassword();
  }
};

const validateConfirmPassword = () => {
  if (!confirmNewPassword.value) {
    errors.value.confirmNewPassword = 'Please confirm your new password.';
    return;
  }

  if (newPassword.value !== confirmNewPassword.value) {
    errors.value.confirmNewPassword = 'Passwords do not match.';
    return;
  }

  delete errors.value.confirmNewPassword;
};

// Watch for password changes to clear errors
watch(newPassword, () => {
  if (errors.value.newPassword) {
    delete errors.value.newPassword;
  }
});

watch(confirmNewPassword, () => {
  if (errors.value.confirmNewPassword) {
    delete errors.value.confirmNewPassword;
  }
});

// Form Submission Logic
const changePassword = async () => {
  // Reset errors
  errors.value = {};

  // Validate all fields
  if (!oldPassword.value) {
    errors.value.oldPassword = 'Current password is required.';
  }

  validateNewPassword();
  validateConfirmPassword();

  // Additional validation
  if (newPassword.value && oldPassword.value === newPassword.value) {
    errors.value.newPassword = 'New password must be different from current password.';
  }

  // If there are validation errors, stop the submission
  if (Object.keys(errors.value).length > 0) {
    showErrorDialog('Please fix the validation errors before proceeding.');
    return;
  }

  loading.value = true;

  try {
    const response = await userStore.ChangePasswordAPI({
      old_password: oldPassword.value,
      new_password: newPassword.value,
      confirm_password: confirmNewPassword.value,
      user_id: authStore.user_pid,
      signature: authStore.hashText(oldPassword.value + newPassword.value + confirmNewPassword.value + authStore.user_pid)
    });

    // Handle successful response
    if (response.success) {
      showSuccessDialog(response.data || 'Your password has been successfully updated. You can now use your new password to access your account.');

      // Reset form fields after successful change
      oldPassword.value = '';
      newPassword.value = '';
      confirmNewPassword.value = '';
      errors.value = {};
    } else {
      showErrorDialog(response.data || 'Failed to update password. Please check your current password and try again.');

      // Reset form fields after failed attempt
      oldPassword.value = '';
      newPassword.value = '';
      confirmNewPassword.value = '';
      errors.value = {};
    }
  } catch (err) {
    // Handle specific error cases
    if (err.message && err.message.includes('Current password is incorrect')) {
      errors.value.oldPassword = 'Current password is incorrect.';
      showErrorDialog('The current password you entered is incorrect. Please verify and try again.');
    } else {
      showErrorDialog(err.message || 'An unexpected error occurred while changing your password. Please try again later.');
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
/* Existing form styles */
.p-password .p-password-meter {
  margin-top: 0.5rem;
}

.p-invalid {
  border-color: #e24c4c;
}

.p-error {
  color: #e24c4c;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
}

.pi-check {
  color: #22c55e;
}

.pi-times {
  color: #ef4444;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.p-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.transition-all {
  transition: all 0.3s ease;
}

/* Modern Dialog Styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

.dialog-container {
  position: relative;
  max-width: 480px;
  width: 100%;
  animation: slideInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.dialog-content {
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  position: relative;
}

.success-dialog {
  border-top: 4px solid #10b981;
}

.error-dialog {
  border-top: 4px solid #ef4444;
}

/* Dialog Header */
.dialog-header {
  position: relative;
  padding: 2rem 2rem 1rem;
  text-align: center;
}

.dialog-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dialog-close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #374151;
  transform: scale(1.1);
}

.dialog-icon-container {
  position: relative;
  display: inline-block;
  margin-bottom: 1rem;
}

.success-icon-wrapper,
.error-icon-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-icon {
  font-size: 2.5rem;
  z-index: 3;
  position: relative;
}

.success-icon {
  color: #10b981;
}

.error-icon {
  color: #ef4444;
}

.success-circle,
.error-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  z-index: 2;
}

.success-circle {
  background: rgba(16, 185, 129, 0.1);
  border: 2px solid #10b981;
  animation: pulseSuccess 2s ease-in-out infinite;
}

.error-circle {
  background: rgba(239, 68, 68, 0.1);
  border: 2px solid #ef4444;
  animation: pulseError 2s ease-in-out infinite;
}

.success-circle-bg,
.error-circle-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90px;
  height: 90px;
  border-radius: 50%;
  z-index: 1;
}

.success-circle-bg {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05));
}

.error-circle-bg {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05));
}

/* Dialog Body */
.dialog-body {
  padding: 0 2rem 1.5rem;
  text-align: center;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.dialog-message {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  max-width: 400px;
  margin: 0 auto;
}

/* Dialog Footer */
.dialog-footer {
  padding: 1.5rem 2rem 2rem;
  text-align: center;
}

.dialog-action-btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.success-btn {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  box-shadow: 0 4px 14px 0 rgba(16, 185, 129, 0.39);
}

.success-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(16, 185, 129, 0.5);
}

.error-btn {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  box-shadow: 0 4px 14px 0 rgba(239, 68, 68, 0.39);
}

.error-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(239, 68, 68, 0.5);
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInScale {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes pulseSuccess {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.05);
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
}

@keyframes pulseError {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.05);
    box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
  }
}

/* Responsive improvements */
@media (max-width: 640px) {
  .max-w-md {
    margin: 0 1rem;
  }

  .p-6 {
    padding: 1rem;
  }

  .dialog-header,
  .dialog-body,
  .dialog-footer {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .dialog-title {
    font-size: 1.25rem;
  }

  .dialog-message {
    font-size: 0.9rem;
  }

  .success-icon-wrapper,
  .error-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .dialog-icon {
    font-size: 2rem;
  }

  .success-circle,
  .error-circle {
    width: 55px;
    height: 55px;
  }

  .success-circle-bg,
  .error-circle-bg {
    width: 70px;
    height: 70px;
  }
}

/* Accessibility improvements */
.dialog-overlay {
  outline: none;
}

.dialog-action-btn:focus {
  outline: 2px solid rgba(59, 130, 246, 0.5);
  outline-offset: 2px;
}

.dialog-close-btn:focus {
  outline: 2px solid rgba(59, 130, 246, 0.5);
  outline-offset: 2px;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .dialog-content {
    background: #1f2937;
    color: #f9fafb;
  }

  .dialog-title {
    color: #f9fafb;
  }

  .dialog-message {
    color: #d1d5db;
  }

  .dialog-close-btn {
    background: rgba(255, 255, 255, 0.1);
    color: #d1d5db;
  }

  .dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #f9fafb;
  }
}
</style>
